import { useAppStore } from '../../../store/useAppStore';
import { logger } from '../../../utils/logger';
import type { ExtractedProp, ExtractedSoundCue } from '../../sync/script-storage-api';

interface MergeOptions {
    mapSceneId?: (sceneId: string) => string;
}

function remapSceneId(sceneId: string | undefined, options?: MergeOptions): string {
    if (!sceneId) return '';
    return options?.mapSceneId ? options.mapSceneId(sceneId) : sceneId;
}

/** Merge extracted props into store, deduplicating by name and scene. */
export function mergeExtractedProps(extractedProps: ExtractedProp[], options?: MergeOptions): void {
    if (extractedProps.length === 0) return;

    const state = useAppStore.getState();
    const propsActions = state.propsActions;
    const resolveCharacterId = buildCharacterResolver(state.showStructure.characters || []);
    const seen = new Set(
        (state.props || []).map(prop =>
            productionElementKey(prop.name, prop.sceneId)
        )
    );

    for (const prop of extractedProps) {
        const sceneId = remapSceneId(prop.sceneId, options);
        const key = productionElementKey(prop.name, sceneId);
        if (seen.has(key)) continue;

        propsActions.addProp({
            name: prop.name,
            sceneId,
            assignedCharacterId: resolveCharacterId(prop.assignedCharacterId) || undefined,
            type: prop.type || 'Hand',
            notes: prop.notes || ''
        });
        seen.add(key);
    }

    logger.store('[scriptImportResults] Merged', extractedProps.length, 'extracted props');
}

/** Merge extracted sound cues into store, deduplicating by description and scene. */
export function mergeExtractedSoundCues(extractedCues: ExtractedSoundCue[], options?: MergeOptions): void {
    if (extractedCues.length === 0) return;

    const soundActions = useAppStore.getState().soundActions;
    const seen = new Set(
        (useAppStore.getState().soundCues || []).map(cue =>
            productionElementKey(cue.description, cue.sceneId)
        )
    );

    for (const cue of extractedCues) {
        const sceneId = remapSceneId(cue.sceneId, options);
        const key = productionElementKey(cue.description, sceneId);
        if (seen.has(key)) continue;

        soundActions.addSoundCue({
            description: cue.description,
            sceneId,
            cueLine: cue.cueLine || '',
            notes: cue.notes || '',
        });
        seen.add(key);
    }

    logger.store('[scriptImportResults] Merged', extractedCues.length, 'extracted sound cues');
}

function productionElementKey(nameOrDescription: unknown, sceneId: unknown): string {
    return `${normalizeForDedupe(nameOrDescription)}::${cleanString(sceneId)}`;
}

function normalizeForDedupe(value: unknown): string {
    return cleanString(value).replace(/[^a-z0-9]+/gi, ' ').trim().replace(/\s+/g, ' ').toUpperCase();
}

function cleanString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function normalizeCharacterId(value: string): string {
    const normalized = value
        .trim()
        .replace(/\s*\([^)]*\)\s*$/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return normalized || 'character';
}

function normalizeText(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

function buildCharacterResolver(characters: Array<{ id: string; name: string }>): (value: unknown) => string | undefined {
    const lookup = new Map<string, string>();

    for (const character of characters) {
        lookup.set(normalizeText(character.name), character.id);
        lookup.set(normalizeCharacterId(character.name), character.id);
        lookup.set(normalizeCharacterId(character.id), character.id);
    }

    return (value: unknown) => {
        const candidate = cleanString(value);
        if (!candidate) return;

        return lookup.get(normalizeText(candidate)) ?? lookup.get(normalizeCharacterId(candidate));
    };
}
